/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AvatarGroup,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Tag,
  TagLabel,
  TagRightIcon,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { pushAndNavigate } from '../../native/SwipeBack'
import { deleteWaitlist, postWaitlist } from '../../api/user/play/PlayExpand'
import useMobile from '../../hooks/useMobile'
import { Calendar, ExternalShare, MessagesFilled } from '../icons/icons'
// import { Browser } from "@capacitor/browser";

import { TbDots, TbCheck, TbKey, TbSearch } from 'react-icons/tb'
import { StatsBox } from '../../pages/user/ProfileActivity'
import { DetailsPopover } from '../datagrid/Datagrid'
import LazyAvatar from '../lazy/LazyAvatar'

import { getEventLabel } from '../../types/EventTypes'
import { getCourtName, type CourtValue } from '../../functions/CourtName'
import UserSelectModal from '../modals/UserSelectModal'
//import { AuthContext } from "../../App";
import { postSessionToken } from '../../api/admin/Events'
import { patchEventCheckIn } from '../../api/user/play/Play'
import InsertPrivateCodeModal from '../modals/InsertPrivateCodeModal'

interface EventUser {
  id: string
  display_name: string
  profile_image: string
  checked_in?: boolean
  follows?: boolean | null
}

interface EventPrivateCoach {
  id: string
  display_name: string
  profile_image: string
}

function toOrdinal(number: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd']
  const lastDigit = number % 10
  const lastTwoDigits = number % 100

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return number + 'th'
  }

  return number + (suffixes[lastDigit] || 'th')
}

const formatDuration = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 1 && minutes === 0) {
    return '1 hour'
  } else if (hours === 2 && minutes === 0) {
    return '2 hours'
  } else if (hours === 3 && minutes === 0) {
    return '3 hours'
  }

  return `${hours > 0 ? `${hours} hr ` : ''}${minutes > 0 ? `${minutes} min` : ''}`.trim()
}

// Local h:mm AM/PM start–end range for the compact layout. Mirrors
// PlayEventCard.formatTimeRange but takes the existing Date + numeric duration.
// Display-only; never calls toISOString().
const pad = (n: number) => String(n).padStart(2, '0')

const formatCompactTimeRange = (start: Date, durationMin: number): string => {
  const end = new Date(start.getTime() + durationMin * 60000)
  const fmt = (d: Date) => {
    let h = d.getHours()
    const m = d.getMinutes()
    const ampm = h >= 12 ? 'PM' : 'AM'
    h = h % 12 || 12
    return `${h}:${pad(m)}${ampm}`
  }
  return `${fmt(start)}-${fmt(end)}`
}

const formatDateTime = (date: Date): string => {
  const today = new Date()
  const tomorrow = new Date(today)

  const isToday = date.toDateString() === today.toDateString()
  const isTomorrow = date.toDateString() === tomorrow.toDateString()
  const longOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: 'numeric',
  }
  const shortOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
  }

  tomorrow.setDate(today.getDate() + 1)
  tomorrow.setHours(12, 30, 0, 0)

  if (isToday) {
    return `Today, ${date.toLocaleTimeString('en-US', shortOptions)}`
  } else if (isTomorrow) {
    return `Tomorrow, ${date.toLocaleTimeString('en-US', shortOptions)}`
  } else {
    return date.toLocaleDateString('en-US', longOptions)
  }
}

// const useTextWidth = (text: string, font: string) => {
//     const [width, setWidth] = useState(0);
//     const canvas = useRef(document.createElement('canvas'));

//     useEffect(() => {
//       const context = canvas.current.getContext('2d');
//       if (context) {
//         context.font = font;
//         const metrics = context.measureText(text);
//         setWidth(metrics.width);
//       }
//     }, [text, font]);

//     return width;
//   };

// const DynamicText = ({ text, maxFontSize } : {
//     text: string;
//     maxFontSize: number;
// }) => {
//     const containerRef = useRef<HTMLDivElement>(null);
//     const [fontSize, setFontSize] = useState(maxFontSize);
//     const textWidth = useTextWidth(text, `${fontSize}px sans-serif`);

//     useEffect(() => {
//       const containerWidth = containerRef.current?.offsetWidth ?? 0;
//       if (textWidth > containerWidth) {
//         setFontSize((prevFontSize) => Math.max(prevFontSize - 1, 10)); // Minimum font size of 10px
//       }
//     }, [textWidth, maxFontSize]);

//     useEffect(() => {
//       setFontSize(maxFontSize); // Reset font size when text or maxFontSize changes
//     }, [text, maxFontSize]);

//     return (
//       <Box ref={containerRef} width="100%" mt={12 - fontSize + "px" }>
//         <Text fontSize={`${fontSize}px`} noOfLines={1} isTruncated variant="subtle" fontWeight="400">
//           {text}
//         </Text>
//       </Box>
//     );
//   };

const TextPair = ({ label, value, width }: { label: string; value: string; width: string }) => {
  const containerRef = useRef<any>(null)
  const [fontSize, setFontSize] = useState<number>(15)

  const adjustFontSize = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth
      const containerHeight = containerRef.current.offsetHeight

      // Check if the text has broken into a new line
      if (width == '50%') {
        if (containerWidth >= 220) {
          setFontSize(15)
        } else {
          const newSize = 15 * (containerWidth / 200)
          setFontSize(newSize)
        }
      } else {
        if (containerHeight >= 30) {
          setFontSize(11.1)
          return
        }
        setFontSize(12)
      }
    }
  }

  useEffect(() => {
    adjustFontSize()
    window.addEventListener('resize', adjustFontSize)
    return () => window.removeEventListener('resize', adjustFontSize)
  }, [])

  return (
    <Flex alignItems="center" gap="4px" w={width} ref={containerRef}>
      <Text variant="header" fontSize={fontSize} fontWeight="530">
        {label}
      </Text>
      {/* <DynamicText text={value} maxFontSize={15} /> */}
      <Text
        variant="subtle"
        fontSize={fontSize}
        fontWeight="400"
        // noOfLines={1}

        //  textOverflow="ellipsis"
        //  whiteSpace="nowrap"
        //  overflow="hidden"
      >
        {value}
      </Text>
    </Flex>
  )
}

const AvatarPair = ({
  label,
  avatars,
  onClick,
  width,
  isMobile,
}: {
  label: string
  avatars: any[]
  onClick: () => void
  width: string
  isMobile?: boolean
}) => {
  const containerRef = useRef<any>(null)
  const [fontSize, setFontSize] = useState(15)

  const adjustFontSize = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth
      const containerHeight = containerRef.current.offsetHeight

      if (width == '50%') {
        if (containerWidth >= 220) {
          setFontSize(15)
        } else {
          const newSize = 15 * (containerWidth / 200)
          setFontSize(newSize)
        }
      } else {
        if (containerHeight >= 30) {
          setFontSize(11.1)
          return
        }
        setFontSize(12)
      }
    }
  }

  useEffect(() => {
    adjustFontSize()
    window.addEventListener('resize', adjustFontSize)
    return () => window.removeEventListener('resize', adjustFontSize)
  }, [])

  // For players, show only first 3 avatars on mobile, 4 on desktop, and +X indicator
  const isPlayers = label.toLowerCase().includes('player')
  const maxAvatars = isPlayers ? (isMobile ? 3 : 4) : avatars.length
  const displayAvatars = isPlayers ? avatars.slice(0, maxAvatars) : avatars
  const remainingCount = isPlayers && avatars.length > maxAvatars ? avatars.length - maxAvatars : 0
  const avatarSize = (fontSize * 26) / 15

  return (
    <Flex alignItems="center" gap="4px" w={width} ref={containerRef}>
      <Text variant="header" fontSize={fontSize} fontWeight="530">
        {label}({avatars.length}):
      </Text>
      <AvatarGroup onClick={onClick} _hover={{ cursor: 'pointer' }} overflow="hidden">
        {displayAvatars.map((avatar: any, index: number) => (
          <>
            <LazyAvatar
              position="relative"
              src={avatar.profile_image}
              key={avatar.id}
              width={avatarSize}
              height={avatarSize}
              border="1px solid #dce2ea"
              zIndex={displayAvatars.length - index}
              bg="white"
              right={index != 0 ? `${10 + (index - 1) * 26 - 16 * (index - 1)}px` : '0px'}
              //onClick={() => navigate(`/user/profile?id=${coach.id}`)}
              //_hover={{cursor : "pointer"}}
            />
          </>
        ))}
        {remainingCount > 0 && (
          <Box
            position="relative"
            width={avatarSize}
            height={avatarSize}
            minWidth={avatarSize}
            minHeight={avatarSize}
            borderRadius="50%"
            bg="#F5F7F9"
            border="1px solid #dce2ea"
            display="flex"
            alignItems="center"
            justifyContent="center"
            right={isMobile ? `${10 + 2 * 26 - 16 * 2 - 6}px` : `${10 + 3 * 26 - 16 * 3 - 6}px`}
            zIndex={0}
            flexShrink={0}
          >
            <Text fontSize={`${fontSize * 0.8}px`} fontWeight="600" color="#171C22">
              +{remainingCount}
            </Text>
          </Box>
        )}
      </AvatarGroup>
    </Flex>
  )
}

const EventDetailCard = ({
  status,
  time,
  type,
  secondary_type,
  court,
  level,
  host,
  coaches,
  players,
  maxPlayers,
  description,
  price,
  duration,
  distance,
  id,
  waitlistPosition,
  //w,
  isPrivateClass,
  onOpenWaitlist,

  location,
  locationName,
  title,
  thumbnail,
  image,

  onExtraOptionsOpen,
  onRefetch,

  calories,
  steps,
  heartRate,
  singleCoach,
  isPrivate,
  onBookClick,
  onShare,
  compact,
}: {
  status:
    | 'full'
    | 'available'
    | 'cancelled_by_member'
    | 'turn'
    | 'finished'
    | 'booked'
    | 'hosted'
    | 'activity'
    | 'pending_payment'
    | 'waitlist'
    | 'cancelled_by_host'
    | 'booked_as_coach'
  waitlistExpiry?: Date | null
  distance?: number
  time: Date
  type: string
  secondary_type?: string
  court: CourtValue
  w?: string
  level: string
  coaches: EventUser[]
  host: EventUser
  players: EventUser[]
  maxPlayers?: number
  description: string
  price: number
  duration: number | string
  id: string
  title?: string
  onRefetch?: () => void
  isPrivateClass?: boolean
  isPrivate?: boolean
  singleCoach?: EventPrivateCoach

  location?: string
  locationName?: string
  thumbnail?: string
  image?: string

  waitlistPosition?: number
  onOpenWaitlist: () => void

  onExtraOptionsOpen?: (v: string) => void

  calories?: number
  steps?: number
  heartRate?: number

  onBookClick?: () => void | null
  onShare?: () => void
  compact?: boolean
}) => {
  const [hideDetails, setHideDetails] = useState(true)

  const navigate = useNavigate()

  const [token, setToken] = useState<string | null>(null)

  useQuery(['getSessionToken'], () => postSessionToken(), {
    onSuccess: (res) => {
      setToken(res?.token ?? null)
    },
    onError: (error) => {
      console.log(error)
      // If token fetch fails (e.g., user not authenticated), token remains null
      // This is okay for public pages
    },
    retry: false, // Don't retry if auth fails
  })

  const isFull = status === 'full' || status === 'turn' || status === 'waitlist'

  const queryClient = useQueryClient()

  const isTablet = useMobile(950)
  const isMobile = useMobile(750)

  const joinWaitlistMutation = useMutation(['joinWaitlist'], postWaitlist, {
    onSuccess: () => {
      onOpenWaitlist()
      queryClient.invalidateQueries(['getVenueEvents'])
      onRefetch && onRefetch()
    },
    onError: () => {},
  })

  const leaveWaitlistMutation = useMutation(['leaveWaitlist'], deleteWaitlist, {
    onSuccess: () => {
      queryClient.invalidateQueries(['getVenueEvents'])
      onRefetch && onRefetch()
    },
    onError: () => {},
  })

  const canCheckIn = status === 'hosted' || status === 'booked_as_coach'

  const [checkedInIds, setCheckedInIds] = useState<Set<string>>(() => {
    return new Set(players.filter((p) => p.checked_in).map((p) => p.id))
  })

  useEffect(() => {
    setCheckedInIds(new Set(players.filter((p) => p.checked_in).map((p) => p.id)))
  }, [players])

  const checkInMutation = useMutation(patchEventCheckIn, {
    onError: () => {
      // Revert on error
      setCheckedInIds(new Set(players.filter((p) => p.checked_in).map((p) => p.id)))
    },
  })

  const handleCheckIn = useCallback(
    (userId: string, checked: boolean) => {
      setCheckedInIds((prev) => {
        const next = new Set(prev)
        if (checked) next.add(userId)
        else next.delete(userId)
        return next
      })
      checkInMutation.mutate({ event_id: id, member_id: userId, checked_in: checked })
    },
    [id, checkInMutation],
  )

  const {
    isOpen: isPlayerGroupMessageOpen,
    onOpen: onOpenPlayerGroupMessage,
    onClose: onClosePlayerGroupMessage,
  } = useDisclosure()

  const {
    isOpen: isCoachGroupMessageOpen,
    onOpen: onOpenCoachGroupMessage,
    onClose: onCloseCoachGroupMessage,
  } = useDisclosure()

  const { isOpen: isPrivateOpen, onOpen: onOpenPrivate, onClose: onClosePrivate } = useDisclosure()

  const { isOpen: isImageLightboxOpen, onOpen: onOpenImageLightbox, onClose: onCloseImageLightbox } = useDisclosure()

  // Shared, status-dependent booking action. Both the non-compact and compact
  // layouts render this SAME element so booking behavior is identical between
  // them (waitlist join/leave, turn Deny/Confirm, private-code modal, checkout
  // via token, onBookClick auth gate, disabled labels). When `fullWidth` is set
  // (compact layout) the primary button stretches with flex="1".
  const renderBookingAction = (fullWidth?: boolean) => {
    if (status === 'waitlist') {
      return (
        <Button
          variant="light"
          size="sm"
          flex={fullWidth ? '1' : undefined}
          _hover={{}}
          _active={{}}
          cursor="not-allowed"
        >
          <Text variant="subtle">
            You're <span style={{ color: '#06C270' }}>{toOrdinal(waitlistPosition ?? 0)}</span> on the waitlist
          </Text>
        </Button>
      )
    }
    if (status === 'turn') {
      return (
        <Flex gap="10px" flex={fullWidth ? '1' : undefined}>
          <Button
            variant="light"
            size="sm"
            onClick={() => leaveWaitlistMutation.mutate({ id: id })}
            isLoading={leaveWaitlistMutation.isLoading}
          >
            Deny
          </Button>
          <Button
            variant="heavy"
            size="sm"
            flex={fullWidth ? '1' : undefined}
            onClick={() => {
              if (onBookClick) {
                const result = onBookClick()
                if (result !== null) {
                  return
                }
              }
              if (isPrivate) {
                onOpenPrivate()
                return
              }
              navigate(`/checkout/play?id=${id}&access_token=${token}`)
            }}
          >
            Confirm
          </Button>
        </Flex>
      )
    }
    if (status === 'finished') {
      return (
        <Button variant="heavy" size="sm" flex={fullWidth ? '1' : undefined} isDisabled>
          Finished
        </Button>
      )
    }
    return (
      <Button
        variant="heavy"
        size="sm"
        flex={fullWidth ? '1' : undefined}
        onClick={() => {
          // If custom onBookClick is provided, use it
          if (onBookClick) {
            const result = onBookClick()
            // If onBookClick returns null, continue with default behavior
            if (result !== null) {
              return
            }
          }

          if (isPrivate && status == 'available') onOpenPrivate()
          else {
            status === 'full'
              ? joinWaitlistMutation.mutate({ id: id })
              : status === 'available'
                ? navigate(`/checkout/play?id=${id}&access_token=${token}`)
                : {}
          }
        }}
        isDisabled={
          status === 'booked' ||
          status === 'pending_payment' ||
          status === 'cancelled_by_member' ||
          status === 'cancelled_by_host' ||
          status === 'booked_as_coach'
        }
        isLoading={joinWaitlistMutation.isLoading}
      >
        {status === 'full'
          ? 'Join waitlist'
          : status === 'booked'
            ? 'Booked'
            : status == 'pending_payment'
              ? 'Pending Payment'
              : status == 'cancelled_by_member'
                ? 'Cancelled by Me'
                : status == 'cancelled_by_host'
                  ? 'Cancelled by Host'
                  : status == 'booked_as_coach'
                    ? 'Invited as Coach'
                    : 'Book Now'}
      </Button>
    )
  }

  // const openCapacitorSite = async (id:string,token:string) => {
  //   await Browser.open({ url: "https://staging.lvbl.club/#" +
  //                             `/checkout/play?id=${id}&access_token=${token}` });
  // };

  return (
    <>
      {/* Image Lightbox Modal */}
      <Modal isOpen={isImageLightboxOpen} onClose={onCloseImageLightbox} size="full" closeOnOverlayClick={true}>
        <ModalOverlay bg="rgba(0, 0, 0, 0.8)" onClick={onCloseImageLightbox} />
        <ModalContent bg="transparent" boxShadow="none" maxW="none" maxH="none" onClick={onCloseImageLightbox}>
          <ModalBody p="0" display="flex" alignItems="center" justifyContent="center" minH="100vh">
            {(image || thumbnail) && (
              <img
                src={image ?? thumbnail}
                alt="Event"
                style={{
                  maxWidth: '95vw',
                  maxHeight: '95vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                }}
                loading={(image ?? thumbnail)?.toLowerCase().endsWith('.gif') ? 'eager' : 'lazy'}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {isPrivateClass && (
        <Box
          bg="white"
          borderRadius="10px"
          boxShadow="0px 12.914606094360352px 86.71235656738281px 0px #2B2C2D14"
          w="100%"
          maxW="100%"
          minW="100%"
          borderWidth="1px"
          borderColor="#DCE2EA"
          overflow="hidden"
          pos="relative"
        >
          <Flex>
            <Flex ml="10px" mt="20px" gap="9px" alignItems={'center'}>
              <LazyAvatar
                src={singleCoach?.profile_image ?? ''}
                key={singleCoach?.id}
                width={26}
                height={26}
                border="1px solid #dce2ea"
                onClick={() => pushAndNavigate(navigate, `/user/profile?id=${singleCoach?.id}`)}
                _hover={{ cursor: 'pointer' }}
              />
              <Text fontWeight={'500'} fontSize="13px">
                {singleCoach?.display_name}
              </Text>
            </Flex>
          </Flex>
          <Flex justifyContent="space-between" p="20px" pb="0">
            <Flex alignItems="center" gap="4px">
              <Text variant="header" fontSize={isTablet ? '12' : '15'}>
                Duration:
              </Text>
              <Text variant="subtle" fontSize={isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                {/* {duration} min */}
                {formatDuration(Number(duration))}
              </Text>
            </Flex>
            <Flex alignItems="center" gap="4px">
              <Text variant="header" fontSize={isTablet ? '12' : '15'}>
                Time:
              </Text>
              <Text variant="subtle" fontSize={isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                {formatDateTime(new Date(time))}
              </Text>
            </Flex>
            <Flex alignItems="center" gap="4px">
              <Text variant="header" fontSize={isTablet ? '12' : '15'}>
                Type:
              </Text>
              <Text variant="subtle" fontSize={isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                {getEventLabel(type)}
              </Text>
            </Flex>
            {secondary_type && (
              <Flex alignItems="center" gap="4px">
                <Text variant="header" fontSize={isTablet ? '12' : '15'}>
                  Secondary Type:
                </Text>
                <Text variant="subtle" fontSize={isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                  {getEventLabel(secondary_type)}
                </Text>
              </Flex>
            )}
          </Flex>

          <Flex justifyContent={'space-between'} p="20px" alignItems="center" gap="10px">
            <Text fontWeight="600" color="#171C22" fontSize="lg">
              {/* {duration} min */}
              {formatDuration(Number(duration))}
            </Text>
          </Flex>
        </Box>
      )}
      {!isPrivateClass && (
        <>
          <InsertPrivateCodeModal
            isOpen={isPrivateOpen}
            onClose={onClosePrivate}
            id={id}
            token={token ?? ''}
            onBook={(private_code: string) => {
              // waitlist, join OR
              // window.location.href =
              //   "https://staging.lvbl.club/#" +
              //   `/checkout/play?id=${id}&access_token=${token}&private_code=${private_code}`;
              // If custom onBookClick is provided, use it
              if (onBookClick) {
                const result = onBookClick()
                if (result !== null) {
                  return
                }
              }
              navigate(`/checkout/play?id=${id}&access_token=${token}&private_code=${private_code}`)
            }}
          />

          <UserSelectModal
            isMobile={isMobile}
            isOpen={isPlayerGroupMessageOpen}
            onClose={onClosePlayerGroupMessage}
            onChange={() => {}}
            title="All players"
            users={players.map((player) => {
              return {
                id: player.id,
                name: player.display_name,
                avatar: player.profile_image,
                follows: player.follows,
              }
            })}
            customIcon={
              <Icon w="20px" h="20px" viewBox="0 0 24 24">
                <MessagesFilled />
              </Icon>
            }
            enableCheckIn={canCheckIn}
            checkedInIds={checkedInIds}
            onCheckIn={handleCheckIn}
            customFooterButtons={
              <Flex gap="12px" w="100%">
                <Button variant="light" size="sm" w="50%">
                  Close
                </Button>
                <Button variant="heavy" size="sm" w="50%">
                  Send group message
                </Button>
              </Flex>
            }
          />
          <UserSelectModal
            isMobile={isMobile}
            isOpen={isCoachGroupMessageOpen}
            onClose={onCloseCoachGroupMessage}
            onChange={() => {}}
            title="All coaches"
            users={coaches.map((player) => {
              return {
                id: player.id,
                name: player.display_name,
                avatar: player.profile_image,
                follows: player.follows,
              }
            })}
            customIcon={
              <Icon w="20px" h="20px" viewBox="0 0 24 24">
                <MessagesFilled />
              </Icon>
            }
            customFooterButtons={
              <Flex gap="12px" w="100%">
                <Button variant="light" size="sm" w="50%">
                  Close
                </Button>
                <Button variant="heavy" size="sm" w="50%">
                  Send group message
                </Button>
              </Flex>
            }
          />
          {compact && status !== 'hosted' && status !== 'activity' ? (
            <Box
              border="1px solid #DCE2EA"
              borderRadius="15px"
              p="15px"
              bg="white"
              w={isMobile ? '100%' : '440px'}
              maxW="100%"
            >
              <Flex gap="20px">
                <Image
                  src={image ?? thumbnail ?? undefined}
                  alt=""
                  boxSize="100px"
                  borderRadius="12px"
                  objectFit="cover"
                  bg="#eff2f5"
                  flexShrink={0}
                  onClick={image || thumbnail ? onOpenImageLightbox : undefined}
                  cursor={image || thumbnail ? 'pointer' : undefined}
                />
                <Box flex="1" minW="0">
                  <Flex justify="space-between" align="flex-start">
                    <Box
                      bg="#EFF2F5"
                      color="#7E8B9A"
                      border="1px solid #DCE2EA"
                      borderRadius="full"
                      px="10px"
                      py="2px"
                      fontSize="11px"
                      fontWeight={500}
                    >
                      {getEventLabel(type)}
                    </Box>
                    {status === 'booked' || status === 'booked_as_coach' ? (
                      <HStack
                        spacing="4px"
                        bg="#E6F2EA"
                        border="1px solid #B7DCC1"
                        borderRadius="full"
                        px="8px"
                        py="2px"
                        color="#1F7A3A"
                      >
                        <Box as={TbCheck} boxSize="12px" />
                        <Text fontSize="11px" fontWeight={700} letterSpacing="0.5px">
                          GOING
                        </Text>
                      </HStack>
                    ) : status === 'full' ? (
                      <Box
                        bg="#FDE1E2"
                        color="#C0392B"
                        borderRadius="full"
                        px="10px"
                        py="2px"
                        fontSize="11px"
                        fontWeight={700}
                        letterSpacing="0.5px"
                      >
                        FULL
                      </Box>
                    ) : status === 'waitlist' ? (
                      <Box
                        bg="#FFF5E5"
                        color="#B7791F"
                        border="1px solid #F6E1B8"
                        borderRadius="full"
                        px="10px"
                        py="2px"
                        fontSize="11px"
                        fontWeight={700}
                        letterSpacing="0.5px"
                      >
                        WAITLIST
                      </Box>
                    ) : null}
                  </Flex>

                  <Text fontSize="16px" fontWeight={500} color="#171c22" noOfLines={2} mt="6px">
                    {title}
                  </Text>
                  <Text fontSize="12px" color="#7e8b9a">
                    {level} · {formatCompactTimeRange(time, Number(duration))}
                  </Text>
                  <HStack spacing="6px" mt="4px" color="#7e8b9a">
                    <Box as="svg" w="14px" h="14px" viewBox="0 0 24 24" fill="none">
                      <Calendar />
                    </Box>
                    <Text fontSize="12px">
                      {time.toLocaleDateString(undefined, { weekday: 'short', month: 'long', day: 'numeric' })}
                    </Text>
                  </HStack>
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" mt="12px" gap="10px">
                {(() => {
                  const coach = coaches[0] ?? host
                  const coachId = coach?.id ?? null
                  const coachRow = (
                    <HStack spacing="8px" minW="0" w="100%">
                      <LazyAvatar
                        src={coach?.profile_image ?? ''}
                        width={32}
                        height={32}
                        border="1px solid #dce2ea"
                        bg="white"
                      />
                      <Box minW="0" textAlign="left">
                        <Text fontSize="13px" fontWeight={500} color="#171c22" noOfLines={1}>
                          {coach?.display_name ?? ''}
                        </Text>
                        <Text fontSize="11px" color="#7e8b9a">
                          Coach
                        </Text>
                      </Box>
                    </HStack>
                  )
                  return coachId ? (
                    <Box
                      as="button"
                      type="button"
                      minW="0"
                      flex="1"
                      bg="transparent"
                      p="0"
                      onClick={() => pushAndNavigate(navigate, `/user/profile?id=${coachId}`)}
                      aria-label={`View ${coach?.display_name ?? 'coach'}'s profile`}
                      cursor="pointer"
                    >
                      {coachRow}
                    </Box>
                  ) : (
                    <Box minW="0" flex="1">
                      {coachRow}
                    </Box>
                  )
                })()}
                <Box w="1px" alignSelf="stretch" bg="#DCE2EA" flexShrink={0} />
                <Box textAlign="center" flexShrink={0}>
                  <Text fontSize="16px" fontWeight={600} color="#171c22">
                    ${price.toFixed(0)}
                  </Text>
                  <Text fontSize="11px" color="#7e8b9a">
                    Per session
                  </Text>
                </Box>
                <Box w="1px" alignSelf="stretch" bg="#DCE2EA" flexShrink={0} />
                <Box
                  as="button"
                  type="button"
                  textAlign="right"
                  flexShrink={0}
                  onClick={() => onOpenPlayerGroupMessage()}
                  aria-label="View all players"
                >
                  {players.length > 0 && (
                    <Flex justify="flex-end" mb="4px">
                      <AvatarGroup size="xs" spacing="-6px">
                        {players.slice(0, 4).map((p) => (
                          <LazyAvatar
                            key={p.id}
                            src={p.profile_image}
                            width={24}
                            height={24}
                            border="1px solid #dce2ea"
                            bg="white"
                          />
                        ))}
                        {players.length > 4 && (
                          <Center
                            boxSize="24px"
                            borderRadius="full"
                            bg="#DCE2EA"
                            border="1px solid #dce2ea"
                            ml="-6px"
                          >
                            <Text fontSize="10px" fontWeight={600} color="#171c22">
                              +{players.length - 4}
                            </Text>
                          </Center>
                        )}
                      </AvatarGroup>
                    </Flex>
                  )}
                  <Text fontSize="13px" fontWeight={500} color="#171c22">
                    {maxPlayers ? `${players.length}/${maxPlayers}` : players.length} Enrolled
                  </Text>
                </Box>
              </Flex>

              {description && (
                <Box mt="12px" userSelect="text">
                  <Text variant="subtle" fontSize="13" flexWrap="wrap">
                    {hideDetails && description.length > 240 ? `${description.slice(0, 240)}...` : description}
                  </Text>
                  {description.length > 240 && (
                    <Button variant="link" fontSize="13px" position="relative" onClick={() => setHideDetails(!hideDetails)}>
                      <Text textDecoration="underline">{hideDetails ? 'Read more' : 'Hide'}</Text>
                    </Button>
                  )}
                </Box>
              )}

              <Flex gap="10px" mt="15px" align="center">
                {renderBookingAction(true)}
                {onShare && (
                  <Flex
                    as="button"
                    type="button"
                    aria-label="Share"
                    onClick={onShare}
                    align="center"
                    justify="center"
                    boxSize="40px"
                    borderRadius="10px"
                    border="1px solid #DCE2EA"
                    bg="white"
                    color="#171c22"
                    flexShrink={0}
                  >
                    <Box as="svg" w="16px" h="16px" viewBox="0 0 17 16" fill="none">
                      <ExternalShare />
                    </Box>
                  </Flex>
                )}
              </Flex>
            </Box>
          ) : (
          <Box
            bg="white"
            borderRadius="10px"
            boxShadow="rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"
            maxW="100%"
            w={
              status != 'activity'
                ? isMobile
                  ? '100%'
                  : '440px'
                : isTablet
                  ? 'calc(100%)'
                  : isMobile
                    ? '100%'
                    : '440px'
            }
            borderWidth={status == 'activity' ? '0.5px' : '1px'}
            borderColor={status == 'activity' ? '#00000033' : '#DCE2EA'}
            overflow="hidden"
          >
            {image && (
              <Box
                w="100%"
                h={isMobile ? '260px' : '440px'}
                overflow="hidden"
                cursor="pointer"
                position="relative"
                onClick={onOpenImageLightbox}
              >
                <img
                  src={image}
                  alt="Event"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  loading={image?.toLowerCase().endsWith('.gif') ? 'eager' : 'lazy'}
                />
                <IconButton
                  aria-label="Enlarge image"
                  icon={<Icon as={TbSearch} />}
                  position="absolute"
                  top="12px"
                  right="12px"
                  size="sm"
                  colorScheme="whiteAlpha"
                  bg="rgba(255, 255, 255, 0.9)"
                  color="gray.700"
                  _hover={{ bg: 'rgba(255, 255, 255, 1)' }}
                  borderRadius="full"
                  onClick={onOpenImageLightbox}
                />
              </Box>
            )}
            {status == 'activity' && (
              <StatsBox
                games={1}
                distance={distance ?? 0}
                heartRate={heartRate ?? 0}
                calories={calories ?? 0}
                steps={steps ?? 0}
              />
            )}

            {status === 'hosted' && !isPrivateClass && (
              <Flex justifyContent="space-between" p={isMobile ? '15px' : '20px'}>
                <DetailsPopover
                  thumbnail={thumbnail ?? ''}
                  name={locationName ?? ''}
                  location={location ?? ''}
                  duration={duration?.toString() ?? '' + ' minutes'}
                  price={price}
                  hostVersion
                />
                <IconButton
                  aria-label="options"
                  icon={<Icon w="22px" h="22px" as={TbDots} />}
                  color="#171C22"
                  variant="unstyled"
                  onClick={() => {
                    if (onExtraOptionsOpen) onExtraOptionsOpen(id)
                  }}
                />
              </Flex>
            )}
            {isFull ? (
              <Center h="38px" w="100%" bg="#F5F7F9">
                <Text variant="subtle" fontSize="13">
                  This event is full
                </Text>
              </Center>
            ) : (
              <></>
            )}
            <Flex gap="8px">
              {title && status != 'hosted' && (
                <Box>
                  {(status === 'booked' || status === 'booked_as_coach') && (
                    <Flex
                      gap="2px"
                      padding="2px 10px"
                      borderRadius="50px"
                      bg="#22C55E"
                      w="fit-content"
                      mt={isMobile ? '15px' : '20px'}
                      ml={isMobile ? '15px' : '20px'}
                    >
                      <Text fontSize="sm" color="white">
                        Going
                      </Text>
                      <Icon as={TbCheck} color="white"></Icon>
                    </Flex>
                  )}
                  <Center>
                    <Box ml={isMobile ? '15px' : '20px'} mb="0" mt={isMobile ? '5px' : '10px'}>
                      <Text variant="title" fontSize="17px" fontWeight="600">
                        {title}
                      </Text>
                    </Box>
                  </Center>
                </Box>
              )}
              {isPrivate && (
                <Center>
                  <Tag
                    size={'lg'}
                    variant="subtle"
                    colorScheme="yellow"
                    border="1px solid #FFD700"
                    mt="20px"
                    borderRadius="128px"
                  >
                    <TagLabel>Private</TagLabel>
                    <TagRightIcon as={TbKey} />
                  </Tag>
                </Center>
              )}
            </Flex>
            {/* <Grid templateColumns="repeat(2, 1fr)" gap={0} p={4}>
            {labels.map((label : any, index : number) => {
                return <GridItem
                key={index}
                colSpan={fullRowItems.includes(index) ? 2 : 1}
                ///ref={(el : any) => (itemRefs.current[index] = el)}
                >
                    <Box
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        >
                        {label}
                    </Box>
                    
              </GridItem>
            })}
        </Grid> */}
            <Box p={isMobile ? '15px' : '20px'} userSelect="text">
              <Flex mb="12px">
                {status == 'hosted' ? (
                  <TextPair label="Title:" value={title ?? ''} width={isMobile ? '55%' : '50%'} />
                ) : (
                  <TextPair label="Location:" value={location ?? ''} width={isMobile ? '55%' : '50%'} />
                )}
                <TextPair label="Court:" value={getCourtName(court)} width={isMobile ? '45%' : '50%'} />
              </Flex>

              <Flex mb="12px">
                {status == 'hosted' ? (
                  <TextPair label="Time:" value={formatDateTime(time)} width={isMobile ? '55%' : '50%'} />
                ) : (
                  <TextPair label="Time:" value={formatDateTime(time)} width={isMobile ? '55%' : '50%'} />
                )}
                <TextPair label="Level:" value={level ?? ''} width={isMobile ? '45%' : '50%'} />
              </Flex>
              <Flex mb="12px">
                {status == 'hosted' ? (
                  <TextPair label="Type:" value={getEventLabel(type)} width={isMobile ? '55%' : '50%'} />
                ) : (
                  <TextPair label="Type:" value={getEventLabel(type)} width={isMobile ? '55%' : '50%'} />
                )}
                {secondary_type ? (
                  <TextPair
                    label="Secondary Type:"
                    value={getEventLabel(secondary_type)}
                    width={isMobile ? '55%' : '50%'}
                  />
                ) : (
                  <AvatarPair
                    label="Host"
                    avatars={[host]}
                    onClick={() => pushAndNavigate(navigate, `/user/profile?id=${host.id}`)}
                    width={isMobile ? '45%' : '50%'}
                    isMobile={isMobile}
                  />
                )}
              </Flex>
              <Flex mb="12px">
                {secondary_type && (
                  <AvatarPair
                    label="Host"
                    avatars={[host]}
                    onClick={() => pushAndNavigate(navigate, `/user/profile?id=${host.id}`)}
                    width={isMobile ? '45%' : '50%'}
                    isMobile={isMobile}
                  />
                )}
                {coaches.length > 0 && (
                  <AvatarPair
                    label="Coaches"
                    avatars={coaches}
                    onClick={() => onOpenCoachGroupMessage()}
                    width={isMobile ? '55%' : '50%'}
                    isMobile={isMobile}
                  />
                )}
              </Flex>
              {status == 'hosted' ? (
                <>
                  <Flex mb="12px">
                    <AvatarPair
                      label="Players"
                      avatars={players}
                      onClick={() => onOpenPlayerGroupMessage()}
                      width={isMobile ? '45%' : '50%'}
                      isMobile={isMobile}
                    />
                  </Flex>
                </>
              ) : (
                <>
                  <Flex alignItems="center" w={coaches.length > 0 ? '100%' : 'calc(100% - 20px)'}>
                    <AvatarPair
                      label="Players"
                      avatars={players}
                      onClick={() => onOpenPlayerGroupMessage()}
                      width={isMobile ? '45%' : '50%'}
                      isMobile={isMobile}
                    />
                  </Flex>
                </>
              )}
            </Box>

            {status != 'hosted' && !isPrivateClass && (
              <>
                <Divider />

                <Box p="20px" userSelect="text">
                  <Text
                    variant="subtle"
                    fontSize="13"
                    //noOfLines={hideDetails ? 1 : 10}
                    flexWrap="wrap"
                  >
                    {hideDetails && description.length > 240 ? `${description.slice(0, 240)}...` : description}
                  </Text>
                  {description && description.length > 240 && (
                    <Button variant="link" fontSize="13px" mb="24px" position="relative">
                      <Text
                        position="absolute"
                        left="0"
                        top={isMobile ? '4px' : '0px'}
                        textDecoration="underline"
                        onClick={() => setHideDetails(!hideDetails)}
                      >
                        {hideDetails ? 'Read more' : 'Hide'}
                      </Text>
                    </Button>
                  )}

                  {status != 'activity' && !isPrivateClass && (
                    <Flex justifyContent="space-between" alignItems="center">
                      <Flex gap="8px" alignItems="center">
                        <Text variant="title" fontSize="17px" fontWeight="600">
                          ${price.toFixed(0)}
                        </Text>
                        <Box bg="#7E8B9A" w="4px" h="4px" borderRadius="2px" />
                        <Text
                          variant="title"
                          fontSize="17px"
                          fontWeight="600"
                          whiteSpace="nowrap" // Prevents text from wrapping
                        >
                          {formatDuration(Number(duration))}
                        </Text>
                      </Flex>
                      <Flex gap="8px" alignItems="center">
                        {onShare && (
                          <IconButton
                            aria-label="Share event"
                            variant="heavy"
                            size="sm"
                            // borderRadius="8px"
                            onClick={onShare}
                            icon={
                              <Icon w="16px" h="16px" viewBox="0 0 17 16" fill="none">
                                <ExternalShare />
                              </Icon>
                            }
                          />
                        )}
                        {renderBookingAction()}
                      </Flex>
                    </Flex>
                  )}
                </Box>
              </>
            )}
          </Box>
          )}
        </>
      )}
    </>
  )
}

export default EventDetailCard
